'use client';

import { cn } from '@core/utils';
import type { Variants } from 'framer-motion';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

import { Image } from '@/components/client';
import { WELLO_IMAGE_PREFIX_URL } from '@/constants';

import styles from './ParticipationSteps.module.scss';

const EASE = [0.16, 1, 0.3, 1] as const;

const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.1 },
  },
};

const staggerItem: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
};

const popIn: Variants = {
  hidden: { opacity: 0, y: 24, scale: 0.96 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.6, ease: [0.34, 1.56, 0.64, 1] },
  },
};

const Step1Icon = () => (
  <svg
    width="80"
    height="80"
    viewBox="0 0 80 80"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_26231_11619)">
      <path
        d="M22.07 69.38L27.285 68.36C28.16 68.19 29.06 68.316 29.86 68.71L31.25 69.4C33.58 70.56 34.23 73.58 32.58 75.59L32.3 75.93C31.67 76.7 30.78 77.21 29.79 77.37L22.92 78.44C22.05 78.58 21.154 78.43 20.38 78.01L20.07 77.84C17.72 76.59 17.21 73.44 19.029 71.5L19.9 70.58C20.48 69.96 21.24 69.54 22.07 69.38Z"
        fill="white"
      />
      <path
        d="M46.26 1.59L26.7 11.19C24.044 12.49 23.16 15.85 24.84 18.3L25.75 19.63C27.28 21.86 26.46 24.93 24.02 26.1C21.54 27.29 20.74 30.44 22.35 32.668L26.58 38.51C27.82 40.22 27.81 42.53 26.56 44.23L16.94 57.36C15.81 58.89 15.68 60.94 16.61 62.6L16.85 63.04C17.9 64.93 20.06 65.9 22.17 65.42L44.36 60.44C44.71 60.36 45.07 60.32 45.43 60.32H51.33C52.15 60.32 52.96 60.11 53.68 59.72L59.83 56.32C60.84 55.77 61.62 54.87 62.03 53.8L67.18 40.27C67.47 39.49 66.74 38.7 65.94 38.94C65.278 39.14 64.62 38.634 64.64 37.94L65.03 25.33C65.06 24.38 64.81 23.45 64.32 22.64L52.543 3.41C51.23 1.27 48.51 0.48 46.26 1.59Z"
        fill="white"
      />
      <path
        d="M31.4 31.86C31.4 22.35 36.77 19.48 43.4 19.48C50.02 19.48 55.4 22.35 55.4 31.86C55.4 38.89 48.84 44.89 45.42 47.53C44.21 48.47 42.58 48.47 41.373 47.53C37.95 44.89 31.4 38.89 31.4 31.86Z"
        fill="#32343A"
      />
      <path
        d="M41.08 28.59C41.03 27.904 41.03 26.49 41.08 25.8C41.61 25.77 42.21 25.77 42.69 25.79C45.44 25.893 46.94 27.38 46.94 30.15C46.94 31.96 46.06 33.33 44.52 33.94C44.52 34.58 44.482 35.28 44.43 35.89C43.78 35.966 42.2 35.966 41.58 35.89C41.456 34.4 41.42 32.61 41.49 31.98L42.13 31.926C43.45 31.82 44.0 31.118 44.0 30.17C44.0 29.16 43.31 28.62 42.42 28.59C41.97 28.57 41.59 28.59 41.08 28.59ZM41.27 38.93C41.27 37.92 42.04 37.13 43.0 37.13C43.97 37.13 44.74 37.92 44.74 38.93C44.74 39.93 43.97 40.72 43.0 40.72C42.04 40.72 41.27 39.93 41.27 38.93Z"
        fill="white"
      />
    </g>
    <defs>
      <clipPath id="clip0_26231_11619">
        <rect width="80" height="80" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const Step2Icon = () => (
  <svg
    width="80"
    height="80"
    viewBox="0 0 80 80"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M40.005 10C23.44 10 10.01 23.43 10.01 40.0C10.01 44.46 11.01 48.73 12.78 52.53C13.48 53.99 13.48 55.69 12.98 57.26L10.175 65.59C9.31 68.19 11.81 70.69 14.41 69.83L22.74 67.03C24.31 66.53 26.01 66.53 27.47 67.23C31.27 68.99 35.54 69.99 40.01 69.99C56.57 69.99 70.005 56.56 70.005 40.0C70.0 23.43 56.57 10 40.005 10ZM34.07 53.33H26.67V45.93L39.935 32.6L47.4 40.07L34.07 53.33ZM52.47 35.03L49.73 37.73L42.27 30.27L44.97 27.53C46.135 26.37 48.0 26.37 49.17 27.53L52.47 30.83C53.635 32 53.635 33.87 52.47 35.03Z"
      fill="white"
    />
  </svg>
);

const Step3Icon = () => (
  <svg
    width="80"
    height="80"
    viewBox="0 0 80 80"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M58.75 10H21.25C13.75 10 10 13.75 10 21.25V58.75C10 66.25 13.75 70 21.25 70H58.75C66.25 70 70 66.25 70 58.75V21.25C70 13.75 66.25 10 58.75 10ZM26.67 50.84C25.29 50.84 24.17 49.72 24.17 48.34C24.17 46.96 25.29 45.84 26.67 45.84C28.05 45.84 29.17 46.96 29.17 48.34C29.17 49.72 28.05 50.84 26.67 50.84ZM26.67 34.17C25.29 34.17 24.17 33.05 24.17 31.67C24.17 30.29 25.29 29.17 26.67 29.17C28.05 29.17 29.17 30.29 29.17 31.67C29.17 33.05 28.05 34.17 26.67 34.17ZM53.33 50.83H36.67C35.29 50.83 34.17 49.71 34.17 48.33C34.17 46.95 35.29 45.83 36.67 45.83H53.33C54.71 45.83 55.83 46.95 55.83 48.33C55.83 49.71 54.71 50.83 53.33 50.83ZM53.33 34.17H36.67C35.29 34.17 34.17 33.05 34.17 31.67C34.17 30.29 35.29 29.17 36.67 29.17H53.33C54.71 29.17 55.83 30.29 55.83 31.67C55.83 33.05 54.71 34.17 53.33 34.17Z"
      fill="white"
    />
  </svg>
);

const ArrowDownIcon = () => (
  <svg
    width="47"
    height="46"
    viewBox="0 0 47 46"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="47" height="46" rx="23" fill="#E4302C" />
    <path
      d="M31.76 25.85L24.08 33.75C24.01 33.83 23.91 33.89 23.81 33.93C23.71 33.98 23.61 34 23.5 34C23.392 34 23.29 33.98 23.18 33.93C23.08 33.89 22.99 33.83 22.92 33.75L15.241 25.85C14.92 25.52 14.92 24.99 15.241 24.66C15.56 24.33 16.08 24.33 16.4 24.66L22.678 31.11V12.85C22.678 12.38 23.05 12 23.5 12C23.95 12 24.32 12.38 24.32 12.85V31.11L30.6 24.66C30.92 24.33 31.44 24.33 31.76 24.66C32.08 24.99 32.08 25.52 31.76 25.85Z"
      fill="white"
    />
  </svg>
);

const NOTICES = [
  '2025년 기부한 고객 대상 이벤트입니다.',
  '인당 1개 채널에 1회만 참여 가능합니다.',
  '중복 참여가 확인되어도 1회만 지급됩니다.',
  'SNS 후기 작성 내용이 확인된 후에 웰포인트가 지급됩니다.',
  '2026년 1월 이후에 포인트가 순차 지급됩니다.',
];

export const ParticipationSteps = () => {
  const [isMounted, setIsMounted] = useState(false);
  const shouldReduceMotion = useReducedMotion();

  // 각 섹션별 ref와 inView
  const headerRef = useRef(null);
  const stepsRef = useRef(null);
  const resultRef = useRef(null);
  const noticesRef = useRef(null);

  const headerInView = useInView(headerRef, { once: true, amount: 0.5 });
  const stepsInView = useInView(stepsRef, { once: true, amount: 0.3 });
  const resultInView = useInView(resultRef, { once: true, amount: 0.5 });
  const noticesInView = useInView(noticesRef, { once: true, amount: 0.5 });

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- 마운트 1회 SSR 하이드레이션 게이트
    setIsMounted(true);
  }, []);

  const canAnimate = (inView: boolean) => isMounted && inView;

  return (
    <section className="flex flex-col items-center gap-20 bg-Gray-black px-24 py-60">
      <div className="mx-auto flex w-full max-w-[375px] flex-col items-center gap-20">
        {/* 헤더 */}
        <motion.div
          ref={headerRef}
          className="flex w-full flex-col gap-12 text-center text-Gray-white"
          initial="hidden"
          animate={canAnimate(headerInView) ? 'visible' : 'hidden'}
          variants={shouldReduceMotion ? {} : fadeInUp}
        >
          <p className="font-600 typo-h7">웰로 고향사랑기부 SNS 인증 이벤트</p>
          <p className="font-800 typo-h4">
            참여방법 따라하고
            <br />
            웰포인트 받아보세요!
          </p>
        </motion.div>

        {/* 스텝 카드 */}
        <motion.div
          ref={stepsRef}
          className="flex w-full flex-col gap-56 rounded-xxlarge bg-Gray-white/20 p-40"
          initial="hidden"
          animate={canAnimate(stepsInView) ? 'visible' : 'hidden'}
          variants={shouldReduceMotion ? {} : staggerContainer}
        >
          {/* STEP 1 */}
          <motion.div
            className="flex flex-col items-center gap-8"
            variants={shouldReduceMotion ? {} : staggerItem}
          >
            <div className="flex flex-col items-center gap-16">
              <span className="rounded-pill bg-R-500 px-8 py-4 font-700 text-Gray-white typo-body3">
                STEP 1
              </span>
              <p className="text-center font-600 text-Gray-200 typo-body2">
                웰로에서 원하는 지역에
                <br />
                고향사랑기부하기!
              </p>
            </div>
            <div className={styles.stepIcon}>
              <Step1Icon />
            </div>
          </motion.div>

          {/* STEP 2 */}
          <motion.div
            className="flex flex-col items-center gap-12"
            variants={shouldReduceMotion ? {} : staggerItem}
          >
            <div className="flex flex-col items-center gap-16">
              <span className="rounded-pill bg-R-500 px-8 py-4 font-700 text-Gray-white typo-body3">
                STEP 2
              </span>
              <div className="flex flex-col items-center gap-4">
                <p className="text-center font-600 text-Gray-200 typo-body2">
                  네이버 블로그, 인스타그램 피드에
                  <br />
                  후기 남기기!
                </p>
                <p className="font-400 text-Gray-500 typo-body3">
                  *포토 후기 작성 가이드 참고
                </p>
              </div>
            </div>
            <div className={styles.stepIcon}>
              <Step2Icon />
            </div>
          </motion.div>

          {/* STEP 3 */}
          <motion.div
            className="flex flex-col items-center gap-8"
            variants={shouldReduceMotion ? {} : staggerItem}
          >
            <div className="flex flex-col items-center gap-16">
              <span className="rounded-pill bg-R-500 px-8 py-4 font-700 text-Gray-white typo-body3">
                STEP 3
              </span>
              <p className="text-center font-600 text-Gray-200 typo-body2">
                하단 구글폼을 통해
                <br />
                이벤트 참여 정보 입력하기!
              </p>
            </div>
            <div className={styles.stepIcon}>
              <Step3Icon />
            </div>
          </motion.div>
        </motion.div>

        {/* 결과 섹션 (화살표 + 결과 카드 + CTA) */}
        <motion.div
          ref={resultRef}
          className="flex w-full flex-col items-center gap-20"
          initial="hidden"
          animate={canAnimate(resultInView) ? 'visible' : 'hidden'}
          variants={shouldReduceMotion ? {} : staggerContainer}
        >
          {/* 화살표 */}
          <motion.div
            className={styles.arrow}
            variants={shouldReduceMotion ? {} : fadeInUp}
          >
            <ArrowDownIcon />
          </motion.div>

          {/* 결과 카드 */}
          <motion.div
            className={cn(
              'flex w-full flex-col items-center justify-center gap-8 rounded-xxlarge bg-Gray-white py-40',
            )}
            variants={shouldReduceMotion ? {} : popIn}
          >
            <p className="font-800 text-R-500 typo-h5">후기 내역 확인 후</p>
            <motion.div
              animate={
                shouldReduceMotion ? {} : { y: [0, -8, 0], rotate: [0, 3, 0] }
              }
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            >
              <Image
                src={`${WELLO_IMAGE_PREFIX_URL}/static/event/home-love/market/insta-coin.png`}
                alt="웰포인트"
                width={120}
                height={120}
              />
            </motion.div>
            <div className="text-center font-700 text-Gray-900 typo-body1">
              <p>
                네이버 블로그 후기{' '}
                <span className="text-R-500">웰포인트 1만원</span>
              </p>
              <p>
                인스타그램 피드{' '}
                <span className="text-R-500">웰포인트 3천원</span>
              </p>
            </div>
          </motion.div>
        </motion.div>

        {/* 안내 문구 */}
        <motion.ul
          ref={noticesRef}
          className="w-full font-500 text-Gray-550 typo-body4"
          initial="hidden"
          animate={canAnimate(noticesInView) ? 'visible' : 'hidden'}
          variants={shouldReduceMotion ? {} : fadeInUp}
        >
          {NOTICES.map((notice, index) => (
            <li key={index} className="ml-20 list-disc">
              {notice}
            </li>
          ))}
        </motion.ul>
      </div>
    </section>
  );
};
